// Tags Quill writes under another name for the same formatting.
const TAG_SYNONYMS = { B: "STRONG", I: "EM" };

// Quill 1 indented a list item with this class. Quill 2 writes the indent as nesting instead.
const LEGACY_INDENT_CLASS = /^ql-indent-(\d+)$/;

// Tags that render nothing when they hold no content and carry no attributes.
const BLANKABLE_TAGS = new Set([
  "P",
  "DIV",
  "SPAN",
  "B",
  "STRONG",
  "I",
  "EM",
  "U",
  "S",
  "UL",
  "OL",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
]);

/**
 * Returns whether `quillHtml`, the HTML Quill produced from `originalHtml`, keeps everything the
 * original had, so the visual editor can take the field over without losing markup.
 *
 * Content is kept when its text survives (whitespace aside) and every element of the original has its
 * own counterpart in Quill's output with the same tag (counting `TAG_SYNONYMS` as one), the same list
 * nesting and number, and at least the original's attributes, a class made only of `LEGACY_INDENT_CLASS` names
 * aside. What Quill adds, such as the `<p>` around bare text or a `draggable` attribute on an embed,
 * does not count against it. Neither does an element that renders nothing, such as an empty `<p> </p>`,
 * nor a `<ul>` or `<ol>` without attributes, whose items' nesting already says where the list is.
 * Blank content is always kept, and content holding an HTML comment never is.
 *
 * @example
 *   isLossless("<b>Hi</b>", "<p><strong>Hi</strong></p>")                  // => true
 *   isLossless('<div class="x">Hi</div>', "<p>Hi</p>")                       // => false
 *   isLossless("<p>A<br>B</p><p><br></p>", "<p>A</p><p>B</p><p><br></p>") // => false
 *
 * @param {string} originalHtml
 * @param {string} quillHtml
 * @returns {boolean}
 */
export function isLossless(originalHtml, quillHtml) {
  if (!originalHtml || !originalHtml.trim()) {
    return true;
  }

  const original = parse(originalHtml);
  const converted = parse(quillHtml);

  if (containsComment(original)) {
    return false;
  }

  if (compactText(original) !== compactText(converted)) {
    return false;
  }

  const unmatched = elements(converted);

  return elements(original)
    .filter((element) => !rendersNothing(element) && !isPlainList(element))
    .every((element) => {
      const index = unmatched.findIndex(
        (candidate) => kind(candidate) === kind(element) && hasAttributesOf(candidate, element),
      );
      if (index === -1) {
        return false;
      }

      unmatched.splice(index, 1);
      return true;
    });
}

// A `<template>` parses without running scripts or fetching images.
function parse(html) {
  const template = document.createElement("template");
  template.innerHTML = html;

  return template.content;
}

function containsComment(fragment) {
  return document.createTreeWalker(fragment, NodeFilter.SHOW_COMMENT).nextNode() !== null;
}

function compactText(fragment) {
  return fragment.textContent.replace(/\s+/g, "");
}

function elements(fragment) {
  return Array.from(fragment.querySelectorAll("*"));
}

// Returns a key shared by elements that render alike apart from their attributes: `<b>` inside a
// list item is `STRONG|1`, the same as a `<strong>` there. A numbered item's key also holds its
// number, so the second item of `<ol><li>A</li><li>B</li></ol>` is `LI|1|2`.
function kind(element) {
  const key = `${TAG_SYNONYMS[element.tagName] || element.tagName}|${listDepth(element)}`;

  return element.parentElement?.tagName === "OL" ? `${key}|${listNumber(element)}` : key;
}

// Counts the items before `item` at its own indent, back to the first one indented less, so a Quill 1
// item after a `ql-indent-1` run continues the numbering of the items above that run.
function listNumber(item) {
  const indent = legacyIndent(item);
  let number = 1;

  for (let sibling = item.previousElementSibling; sibling; sibling = sibling.previousElementSibling) {
    const siblingIndent = legacyIndent(sibling);

    if (siblingIndent < indent) {
      break;
    }

    if (siblingIndent === indent) {
      number += 1;
    }
  }

  return number;
}

function hasAttributesOf(candidate, element) {
  return Array.from(element.attributes).every((attribute) => {
    // Quill writes the indent as nesting, which `kind` already compares.
    if (attribute.name === "class" && element.classList.length === legacyIndentClasses(element).length) {
      return true;
    }

    return candidate.getAttribute(attribute.name) === attribute.value;
  });
}

function legacyIndentClasses(element) {
  return Array.from(element.classList).filter((name) => LEGACY_INDENT_CLASS.test(name));
}

function legacyIndent(element) {
  const [indentClass] = legacyIndentClasses(element);

  return indentClass ? Number(indentClass.match(LEGACY_INDENT_CLASS)[1]) : 0;
}

// Quill 1 kept each list type in its own container, so a bullet item indented under a numbered one
// sits in a `<ul>` next to the `<ol>`, where Quill 2 nests it.
function isPlainList(element) {
  return (element.tagName === "UL" || element.tagName === "OL") && element.attributes.length === 0;
}

// Returns whether the element renders nothing: a `BLANKABLE_TAGS` element with no attributes that holds
// only whitespace. A non-breaking space counts as content, because `<p>&nbsp;</p>` is a visible empty line.
function rendersNothing(element) {
  return (
    BLANKABLE_TAGS.has(element.tagName) &&
    element.attributes.length === 0 &&
    element.children.length === 0 &&
    !/[^ \t\n\r\f]/.test(element.textContent)
  );
}

// Returns the number of lists enclosing the element, plus the levels a `ql-indent-N` class on it or
// an enclosing item adds, so a list item only matches an item at the same nesting level in Quill's
// output: `<li class="ql-indent-1">` directly in a `<ul>` is at depth 2.
function listDepth(element) {
  let depth = legacyIndent(element);

  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    if (parent.tagName === "OL" || parent.tagName === "UL") {
      depth += 1;
    }
    depth += legacyIndent(parent);
  }

  return depth;
}
